import { View } from 'react-native';
import { Text } from 'shared/components/text';
import { useCallback, useState } from 'react';
import Banner from 'shared/components/ad-banner/ad-banner.web';
import { useAdAnimal, type AdAnimal } from 'shared/components/ad-banner/ad-animal-context';
import type { AdBannerProps } from 'shared/components/ad-banner/types';

export type AdBannerSize = {
  width: number;
  height: number;
};

const AVAILABLE_SIZES: AdBannerSize[] = [
  { width: 1280, height: 240 },
  { width: 980, height: 240 },
  { width: 600, height: 240 },
  { width: 320, height: 320 },
];
const MOBILE_STICKY_TRACK_HEIGHT = 640;

function getLargestSize(width: number): AdBannerSize | null {
  const candidates = AVAILABLE_SIZES.filter((s) => s.width <= width);

  if (candidates.length === 0) {
    return null;
  }

  return candidates.reduce((best, current) => {
    const bestArea = best.width * best.height;
    const currentArea = current.width * current.height;
    return currentArea > bestArea ? current : best;
  });
}

export const AdBanner = ({ className, loadImmediately = false, placement }: AdBannerProps) => {
  const animal = useAdAnimal();

  return (
    <AnimalBanner
      key={animal}
      animal={animal}
      className={className}
      loadImmediately={loadImmediately}
      sticky={placement !== 'top'}
    />
  );
};

function AnimalBanner({
  animal,
  className,
  loadImmediately,
  sticky,
}: Omit<AdBannerProps, 'placement'> & {
  animal: AdAnimal;
  loadImmediately: boolean;
  sticky: boolean;
}) {
  const [bannerSize, setBannerSize] = useState<AdBannerSize | null>(null);
  const [hasRenderedBanner, setHasRenderedBanner] = useState(false);
  const handleRendered = useCallback(() => setHasRenderedBanner(true), []);

  const handleLayout = useCallback((e: any) => {
    const newSize = getLargestSize(e.nativeEvent.layout.width + 2);
    if (!newSize) {
      setBannerSize(null);
      setHasRenderedBanner(false);
      return;
    }

    setBannerSize((prevSize) => {
      if (!prevSize) {
        return newSize;
      }

      const isSameSize = prevSize.width === newSize.width && prevSize.height === newSize.height;

      if (!isSameSize) {
        setHasRenderedBanner(false);
      }

      return isSameSize ? prevSize : newSize;
    });
  }, []);

  const isMobileSticky = sticky && bannerSize?.width === 320 && bannerSize.height === 320;

  return (
    <View className={`ad-banner-container w-full items-center ${className}`}>
      <View
        className={`ad-banner-track w-full ${sticky ? 'ad-banner-track-sticky' : ''}`}
        style={isMobileSticky ? { height: MOBILE_STICKY_TRACK_HEIGHT } : undefined}
        testID={isMobileSticky ? 'mobile-sticky-ad-track' : undefined}
      >
        <View
          className={`ad-banner-slot z-20 w-full items-center justify-center overflow-hidden rounded ${!hasRenderedBanner ? 'ring-brand-brown-dark/15 bg-white ring-1 ring-inset' : ''} ${isMobileSticky ? 'sticky top-[calc(var(--header-h)+0.75rem)]' : ''}`}
          onLayout={handleLayout}
          testID={isMobileSticky ? 'mobile-sticky-ad' : undefined}
        >
          {bannerSize && (
            <Banner
              animal={animal}
              width={bannerSize.width}
              height={bannerSize.height}
              isRendered={hasRenderedBanner}
              loadImmediately={loadImmediately}
              onRendered={handleRendered}
            />
          )}
          {!hasRenderedBanner && (
            <View className={'absolute inset-0 z-10 items-center justify-center'}>
              <Text className={'text-brand-brown-dark/10 text-2xl font-black uppercase'}>
                Sponsored Ad
              </Text>
            </View>
          )}
        </View>
      </View>
    </View>
  );
}
